import React, { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "../../widgets/modal";
import Loader from "../../widgets/Loader";
import {
  confirmReadyForMulchScheduled,
  fetchReadyForMulchDescription,
} from "./scheduling.utils";
import Swal from "sweetalert2";
import { SCHEDULING_DIALOGS } from "../../constants/dialog.constants";

type SchedulingReadyForMulchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  scheduledId: number;
  propertyName?: string;
};

const isSuccessfulResponse = (result: any) =>
  result?.messages?.[0]?.code === "0";

const getFailureMessage = (result: any) =>
  result?.messages?.[0]?.message ||
  result?.message ||
  SCHEDULING_DIALOGS.propertyReadinessConfirmFailed;

const SchedulingReadyForMulchModal: FC<SchedulingReadyForMulchModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  scheduledId,
  propertyName,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: readyForMulchDescription, isFetching, error } = useQuery({
    queryKey: ["READY_FOR_MULCH_DESCRIPTION"],
    queryFn: fetchReadyForMulchDescription,
    enabled: isOpen,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const descriptionText =
    readyForMulchDescription?.trim() ||
    SCHEDULING_DIALOGS.readyForMulchNoDescription;

  const handleClose = () => {
    onClose();
  };

  const handleConfirmPropertyReadiness = async () => {
    if (!scheduledId) {
      Swal.fire({
        customClass: ".swal2-confirm",
        icon: "warning",
        title: SCHEDULING_DIALOGS.readinessMissingJob,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await confirmReadyForMulchScheduled(scheduledId);
      if (isSuccessfulResponse(result)) {
        Swal.fire({
          customClass: {
            popup: "sweetAlert",
          },
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          title: SCHEDULING_DIALOGS.propertyReadinessConfirmed,
        });
        await onSubmit();
        onClose();
      } else {
        Swal.fire({
          customClass: ".swal2-confirm",
          icon: "warning",
          title: getFailureMessage(result),
        });
      }
    } catch (confirmError) {
      console.error(
        SCHEDULING_DIALOGS.propertyReadinessConfirmFailed,
        confirmError
      );
      Swal.fire({
        customClass: ".swal2-confirm",
        icon: "warning",
        title: SCHEDULING_DIALOGS.propertyReadinessConfirmFailed,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      label={`${SCHEDULING_DIALOGS.readyForMulchLabel}${propertyName ? ` (${propertyName})` : ""}`}
      onClose={handleClose}
      className="custom-modal scheduling-ready-for-mulch-modal scheduling-status-confirmation-modal scheduling-ready-for-mulch-confirmation-modal"
    >
      <div className="requestModalSection">
        <div className="modalForm">
          {isFetching || isSubmitting ? <Loader /> : null}
          {error ? (
            <div className="text-danger">
              {SCHEDULING_DIALOGS.readyForMulchLoadFailed}
            </div>
          ) : (
            <div className="scheduling-description-content">
              <div className="scheduling-description-block">
                <div className="scheduling-description-text scheduling-status-popup-text">
                  {descriptionText}
                </div>
              </div>
            </div>
          )}
          <div className="scheduling-priority-footer">
            <div className="scheduling-modal-actions scheduling-priority-actions">
              <button
                type="button"
                className="btn cancelPwdBtn"
                onClick={handleClose}
              >
                {SCHEDULING_DIALOGS.closeButton}
              </button>
              <button
                type="button"
                className="btn submitBtn"
                onClick={handleConfirmPropertyReadiness}
                disabled={isSubmitting}
              >
                {SCHEDULING_DIALOGS.confirmPropertyReadiness}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingReadyForMulchModal;
